import React, { useCallback, useEffect, useRef, useState } from 'react';
import { askService, AskError, AskEvent } from '../services/api';
import { AskEngineInfo, AskResult, AskToolCall } from '../types';
import ScopeFilter, { ScopeSelection } from './ScopeFilter';

// Ask questions of the recordings in plain English.
//
// The design problem here is not the chat box — it is trust. A generated
// answer about someone's phone calls is worth nothing if you cannot see what
// it looked at, and worse than nothing if it quietly leaves things out. So
// three things are always visible next to the answer rather than tucked away:
//
//   * WHAT IT SEARCHED. Every tool call, with its arguments, expandable.
//     "Did it actually look at the right domain" is the first question anyone
//     asks, and a panel is cheaper than asking it.
//   * WHAT IT COST. Turns, conversations read, tokens and money. A feature
//     that spends money on each use should say so where the use happens, not
//     in a report someone reads monthly.
//   * WHAT IT COULD NOT SEE. Capped samples, refusals, and citations the
//     model produced that no tool returned.
//
// Where the host offers more than one engine — Gemini, or one of the models
// running on this machine — the choice is made per question, and every answer
// says which engine and model wrote it. Changing engine does not clear the thread: asking the same
// question both ways, one under the other, is the point of offering the choice.
//
// The scope is stamped on the thread and changing it clears the thread: a
// question answered against one territory means something different answered
// against another, and letting the two sit together in one list invites
// reading the second as though it answered the first.

interface Props {
  // Opens a cited conversation in the VCons tab. Lifted to App because the
  // tabs are siblings — the alternative is duplicating the vCon detail view
  // here, which would then drift from the real one.
  onOpenVcon: (uuid: string) => void;
}

interface ThreadEntry {
  question: string;
  result?: AskResult;
  error?: string;
  // Distinguishes "you may not" and "you have spent enough" from a fault, so
  // the page can say something useful rather than showing a red box.
  kind?: 'forbidden' | 'capped' | 'upstream' | 'error';
}

const nf = new Intl.NumberFormat();

// The model writes prose with **emphasis**, *italics* and `code`, because that
// is how models write. Rendering it verbatim puts asterisks on the screen; the
// alternative — telling it not to — throws away structure that genuinely helps
// on a long answer with several named components in it.
//
// Deliberately the smallest possible renderer, and it builds React elements
// rather than HTML: the text originates from a model reasoning over recorded
// conversations, which is to say it is untrusted, and there is no version of
// dangerouslySetInnerHTML that is worth the convenience here.
function renderInline(text: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  const pattern = /(\*\*[^*]+\*\*|`[^`]+`|\*[^*]+\*)/g;
  let last = 0;
  let match: RegExpExecArray | null;
  let key = 0;
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > last) out.push(text.slice(last, match.index));
    const token = match[0];
    if (token.startsWith('**')) {
      out.push(<strong key={key++}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith('`')) {
      out.push(<code className="monospace" key={key++}>{token.slice(1, -1)}</code>);
    } else {
      out.push(<em key={key++}>{token.slice(1, -1)}</em>);
    }
    last = pattern.lastIndex;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function scopeLabel(scope: ScopeSelection): string {
  const t = scope.territory || 'All territories';
  const d = scope.domain || 'all domains';
  return `${t} · ${d}`;
}

// "Gemini · gemini-2.5-pro" or "Local · gpt-oss-20b-ask", for an answer's footer.
function engineLabel(result: AskResult): string {
  return `${result.engine === 'local' ? 'Local' : 'Gemini'} · ${result.models.answer}`;
}

function engineKey(e: AskEngineInfo): string {
  return e.local ? `local:${e.model}` : e.engine;
}

function money(usd: number | null): string {
  if (usd === null) return 'not priced';
  return usd < 0.01 ? `$${usd.toFixed(4)}` : `$${usd.toFixed(3)}`;
}

// The tool trail, as one line each. Arguments are shown because they are the
// interesting part — "it searched for the wrong word" and "it searched the
// wrong domain" are both invisible without them.
const ToolTrail: React.FC<{ calls: AskToolCall[] }> = ({ calls }) => (
  <table className="ask-trail">
    <tbody>
      {calls.map((c, i) => {
        const args = Object.entries(c.args)
          .filter(([, v]) => v !== '' && v !== null && v !== undefined);
        return (
          <tr key={i} className={c.refused ? 'ask-trail-refused' : undefined}>
            <td className="ask-trail-n">{i + 1}</td>
            <td className="monospace">{c.tool}</td>
            <td className="monospace wrap ask-trail-args">
              {args.length
                ? args.map(([k, v]) => `${k}: ${JSON.stringify(v)}`).join('  ')
                : <span style={{ opacity: 0.4 }}>no arguments</span>}
            </td>
            <td className="ask-trail-ms">{c.ms}ms</td>
            <td className="ask-trail-flag">
              {c.refused && <span title="This call was refused — see the answer for why">refused</span>}
            </td>
          </tr>
        );
      })}
    </tbody>
  </table>
);

const AskPage: React.FC<Props> = ({ onOpenVcon }) => {
  const [scope, setScope] = useState<ScopeSelection>({});
  const [question, setQuestion] = useState('');
  const [asController, setAsController] = useState(false);
  // What this host offers. Empty until loaded, and a single entry on a host
  // with no local model — in both cases there is no choice to show.
  const [engines, setEngines] = useState<AskEngineInfo[]>([]);
  // The chosen entry, keyed 'vertex' or 'local:<model>' — two local models
  // share an engine, so the engine alone cannot say which was picked.
  const [choice, setChoice] = useState<string | null>(null);
  const [thread, setThread] = useState<ThreadEntry[]>([]);
  const [busy, setBusy] = useState(false);
  // The question currently in flight. Shown above the spinner: a question that
  // disappears the moment it is asked leaves the reader with nothing to check
  // the answer against when it arrives 40 seconds later.
  const [pending, setPending] = useState<string | null>(null);
  // What the loop is doing right now, rebuilt from the event stream. The tool
  // calls accumulate as they complete, so the wait shows the shape of the
  // search forming rather than a spinner that could mean anything.
  const [live, setLive] = useState<{ phase: string; turn: number; calls: AskToolCall[] }>(
    { phase: '', turn: 0, calls: [] });
  const [openTrail, setOpenTrail] = useState<number | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);

  // A thread belongs to the scope it was asked in. Rather than labelling each
  // answer with a scope that may no longer match the pulldowns, the thread
  // resets — which is also the honest signal that the previous answers are not
  // about what is now selected.
  const scopeKey = `${scope.territory || ''}/${scope.domain || ''}`;
  const previousScope = useRef(scopeKey);
  useEffect(() => {
    if (previousScope.current !== scopeKey) {
      previousScope.current = scopeKey;
      setThread([]);
      setOpenTrail(null);
    }
  }, [scopeKey]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [thread, busy]);

  useEffect(() => {
    let cancelled = false;
    askService.engines()
      .then(r => {
        if (cancelled) return;
        setEngines(r.engines);
        const preferred = r.engines.find(e => e.default && e.available)
          ?? r.engines.find(e => e.available);
        setChoice(preferred ? engineKey(preferred) : null);
      })
      // An older API without the GET: no choice, and the host default answers.
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // A choice is offered only between engines that can answer now. A local one
  // that is listed but not running is named underneath instead, so the reader
  // learns what to start rather than wondering where the option went.
  const usable = engines.filter(e => e.available);
  const unavailable = engines.filter(e => !e.available);
  const offerChoice = usable.length > 1;
  const chosen: AskEngineInfo | undefined =
    offerChoice ? usable.find(e => engineKey(e) === choice) : undefined;

  const submit = useCallback(async () => {
    const q = question.trim();
    if (!q || busy) return;
    setBusy(true);
    setPending(q);
    setQuestion('');
    setLive({ phase: 'thinking', turn: 1, calls: [] });
    try {
      const result = await askService.askStream(q, scope, asController, event => {
        if (event.name === 'thinking') {
          setLive(prev => ({ ...prev, phase: 'thinking', turn: event.turn }));
        } else if (event.name === 'tool') {
          setLive(prev => ({ ...prev, phase: `running ${event.tool}` }));
        } else if (event.name === 'tool_done') {
          setLive(prev => ({
            ...prev,
            phase: 'thinking',
            calls: [...prev.calls, {
              tool: event.tool, args: event.args, ms: event.ms, refused: event.refused,
            }],
          }));
        } else if (event.name === 'writing') {
          setLive(prev => ({ ...prev, phase: 'writing the answer' }));
        }
      }, chosen?.engine, chosen?.local ? chosen.model : undefined);
      setThread(prev => [...prev, { question: q, result }]);
    } catch (e: any) {
      const status = e instanceof AskError ? e.status : (e?.response?.status ?? 0);
      const message = (e instanceof AskError ? e.message : e?.response?.data?.error)
        || 'The question could not be answered.';
      const kind = status === 403 ? 'forbidden'
        : status === 429 ? 'capped'
        : status === 502 ? 'upstream'
        : 'error';
      setThread(prev => [...prev, { question: q, error: message, kind }]);
    } finally {
      setBusy(false);
      setPending(null);
      setLive({ phase: '', turn: 0, calls: [] });
    }
  }, [question, busy, scope, asController, chosen]);

  const latestBudget = [...thread].reverse()
    .find(e => e.result?.budget)?.result?.budget ?? null;

  return (
    <div className="ask-page">
      <div className="ask-head">
        <div>
          <h2>Ask</h2>
          <p className="small ask-sub">
            Questions about the recorded conversations, answered from the
            transcripts and email bodies in the selected scope. Every answer
            cites what it used, and shows what it searched to get there.
          </p>
        </div>
      </div>

      <ScopeFilter value={scope} onChange={setScope}>
        <span className="small" style={{ opacity: 0.7 }}>
          Changing this starts a new thread.
        </span>
      </ScopeFilter>

      <div className="ask-thread">
        {thread.length === 0 && !busy && (
          <div className="ask-empty">
            <p className="small">
              Try: <em>“what do people call about most?”</em> ·{' '}
              <em>“how many conversations mention billing, by month?”</em> ·{' '}
              <em>“who calls most often, and when were they last in touch?”</em>
            </p>
            <p className="small" style={{ opacity: 0.65 }}>
              Answers are drawn only from conversations that have searchable
              text. Ask what is in scope first if you want to know how much
              that is.
            </p>
          </div>
        )}

        {thread.map((entry, i) => (
          <div className="ask-entry" key={i}>
            <div className="ask-q">
              <span className="ask-q-mark">?</span>
              <span>{entry.question}</span>
            </div>

            {entry.error && (
              <div className={`ask-a ask-a-${entry.kind}`}>
                <p>{entry.error}</p>
                {entry.kind === 'capped' && (
                  <p className="small">
                    The limit is on tokens rather than questions, so a few large
                    questions reach it faster than many small ones. It resets on
                    a rolling 24-hour basis.
                  </p>
                )}
              </div>
            )}

            {entry.result && (
              <div className="ask-a">
                {/* Preserve the model's paragraphing — it writes in prose and
                    collapsing that to one block makes a long answer unreadable. */}
                {entry.result.answer.split('\n').map((line, j) =>
                  line.trim()
                    ? <p key={j}>{renderInline(line)}</p>
                    : <div key={j} className="ask-a-gap" />
                )}

                {entry.result.caveats && (
                  <p className="ask-caveat">
                    <strong>Caveats.</strong> {entry.result.caveats}
                  </p>
                )}

                {entry.result.turns_capped && (
                  <p className="ask-caveat ask-caveat-warn">
                    This answer was cut short: it reached the limit on how many
                    steps one question may take, so the search was not
                    exhaustive.
                  </p>
                )}

                {entry.result.ungrounded_citations.length > 0 && (
                  <p className="ask-caveat ask-caveat-warn">
                    <strong>Partly ungrounded.</strong> The answer referred to{' '}
                    {entry.result.ungrounded_citations.length} conversation
                    {entry.result.ungrounded_citations.length === 1 ? '' : 's'}{' '}
                    that no search returned. Those references have been removed;
                    read the rest with that in mind.
                  </p>
                )}

                {entry.result.citations.length > 0 && (
                  <div className="ask-cites">
                    <span className="small ask-cites-label">Based on</span>
                    {entry.result.citations.map(uuid => (
                      <button
                        key={uuid}
                        className="ask-cite"
                        title="Open this conversation in the VCons tab"
                        onClick={() => onOpenVcon(uuid)}
                      >
                        {uuid.slice(0, 8)}
                      </button>
                    ))}
                  </div>
                )}

                <div className="ask-meta">
                  <button
                    className="ask-trail-toggle"
                    onClick={() => setOpenTrail(openTrail === i ? null : i)}
                  >
                    {openTrail === i ? '▾' : '▸'} what it searched
                    {' '}({entry.result.tool_calls.length})
                  </button>
                  <span className="ask-meta-sep">·</span>
                  <span title="Model calls made to plan and answer">
                    {entry.result.turns} turn{entry.result.turns === 1 ? '' : 's'}
                  </span>
                  <span className="ask-meta-sep">·</span>
                  <span title="Conversations whose text was sent to the model — each one is ledgered as a disclosure">
                    {entry.result.conversations_read} read
                  </span>
                  <span className="ask-meta-sep">·</span>
                  <span title={`${nf.format(entry.result.usage.thoughts)} of these were reasoning tokens, billed at the output rate`}>
                    {nf.format(entry.result.usage.total)} tokens
                  </span>
                  <span className="ask-meta-sep">·</span>
                  <span>{money(entry.result.cost_usd)}</span>
                  <span className="ask-meta-sep">·</span>
                  <span>{(entry.result.duration_ms / 1000).toFixed(1)}s</span>
                  {entry.result.engine && (
                    <>
                      <span className="ask-meta-sep">·</span>
                      <span
                        className={entry.result.engine === 'local' ? 'ask-engine-local' : undefined}
                        title={entry.result.engine === 'local'
                          ? 'Answered by a model on this host — no conversation text left it'
                          : `Answered by Gemini on Vertex AI; planned with ${entry.result.models.planner}`}
                      >
                        {engineLabel(entry.result)}
                      </span>
                    </>
                  )}
                </div>

                {openTrail === i && <ToolTrail calls={entry.result.tool_calls} />}
              </div>
            )}
          </div>
        ))}

        {busy && (
          <div className="ask-entry">
            {pending && (
              <div className="ask-q">
                <span className="ask-q-mark">?</span>
                <span>{pending}</span>
              </div>
            )}
            <div className="ask-a">
              {live.calls.length > 0 && <ToolTrail calls={live.calls} />}
              <div className="ask-a-busy">
                <span className="ask-spinner" />
                <span>
                  {live.phase === 'writing the answer'
                    ? 'Writing the answer…'
                    : live.phase.startsWith('running ')
                      ? <>Running <span className="monospace">{live.phase.slice(8)}</span>…</>
                      : `Working out what to look at (step ${live.turn})…`}
                </span>
                <span className="small" style={{ opacity: 0.6 }}>
                  {live.calls.length > 0
                    ? `${live.calls.length} search${live.calls.length === 1 ? '' : 'es'} so far`
                    : chosen?.local
                      ? 'a local model usually takes 1–5 minutes'
                      : 'usually 10–45 seconds'}
                </span>
              </div>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <div className="ask-compose">
        <textarea
          className="ask-input"
          value={question}
          placeholder={`Ask about ${scopeLabel(scope)}…`}
          rows={2}
          disabled={busy}
          onChange={e => setQuestion(e.target.value)}
          onKeyDown={e => {
            // Enter sends, Shift+Enter breaks the line. A question is usually
            // one sentence, so sending is the common case.
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
        />
        <button
          className="ask-send"
          onClick={submit}
          disabled={busy || !question.trim()}
        >
          {busy ? 'Asking…' : 'Ask'}
        </button>
      </div>

      <div className="ask-foot">
        {offerChoice && (
          <div className="ask-engine" role="radiogroup" aria-label="Which model answers">
            <span className="small ask-engine-label">Answer with</span>
            {usable.map(e => (
              <button
                key={engineKey(e)}
                role="radio"
                aria-checked={chosen === e}
                aria-label={e.label}
                className={`ask-engine-opt${chosen === e ? ' active' : ''}`}
                disabled={busy}
                title={e.local
                  ? `${e.model}, running on this host. Conversation text stays on this machine; slower, no cost.`
                  : `${e.model} on Vertex AI. Faster and stronger on analytical questions; billed per question.`}
                onClick={() => setChoice(engineKey(e))}
              >
                {e.label}
              </button>
            ))}
          </div>
        )}
        {unavailable.length > 0 && (
          <span className="small ask-engine-off">
            {unavailable.map(e => `${e.label} unavailable: ${e.reason}`).join(' · ')}
          </span>
        )}
        <label className="ask-controller" title="Reads conversations whose parties have not granted the analysis purpose. Never overrides a revocation, and every read is recorded in the conversation's ledger.">
          <input
            type="checkbox"
            checked={asController}
            onChange={e => setAsController(e.target.checked)}
          />
          <span>
            Read without an analysis consent grant
            <span className="small" style={{ opacity: 0.65 }}>
              {' '}— as Data Controller. Recorded against each conversation.
              Revocations still apply.
            </span>
          </span>
        </label>

        {latestBudget && (
          <span className="small ask-budget" title="Rolling 24-hour limit on this account">
            {nf.format(latestBudget.tokens_today)} of{' '}
            {nf.format(latestBudget.daily_token_limit)} tokens used today
          </span>
        )}
      </div>
    </div>
  );
};

export default AskPage;
